import { useState } from 'react';
import { DateTime } from 'luxon';
import TravellerCategorySelector from '../Shared/TravellerCategorySelector';
import { TravellerCategory } from '../../models/traveller-category.enum';
import { TranslationService } from '../../services/translation.service';
import Currency from '../Shared/Currency';
import { ConfigurationService } from '../../services/configuration.service';
import type { IProduct } from '../../models/product.model';
import ProductViewModel from './models/product.view-model';
import type { ProductType } from '../../models/product-type.enum';
import type { ITimeValidity } from '../../models/time-validity.model';
import { TimeValidityType } from '../../models/time-validity-type.enum';
import { TimeValidityUnit } from '../../models/time-validity-unit.enum';
import type { ITariffZone } from '../../models/tariff-zone.model';
import ShowMoreButton from '../Shared/ShowMoreButton';

enum SortBy {
  type,
  validitytime,
  zone,
  price,
}

enum SortDirection {
  ascending,
  descending,
}

function getAriaSortValue(column: SortBy, sortBy: SortBy, sortDirection: SortDirection): 'ascending' | 'descending' | 'none' | 'other' | undefined {
  if (column !== sortBy) {
    return undefined;
  }
  return sortDirection === SortDirection.ascending ? 'ascending' : 'descending';
}

function translateProductType(productType: ProductType): string {
  return TranslationService.get(`common.products.types.${productType.toLocaleString().toLocaleLowerCase()}`);
}

function convertTimeValidityToMinutes(timeValidity: ITimeValidity): number {
  switch (timeValidity.type) {
    case TimeValidityType.amountUnit:
      switch (timeValidity.unit) {
        case TimeValidityUnit.minutes:
          return timeValidity.amount!;
        case TimeValidityUnit.hours:
          return timeValidity.amount! * 60;
        case TimeValidityUnit.days:
          return timeValidity.amount! * 60 * 24;
        case TimeValidityUnit.semester:
          return timeValidity.amount! * 60 * 24 * 150; // Only for comparison
        case TimeValidityUnit.schoolYear:
          return timeValidity.amount! * 60 * 24 * 300; // Only for comparison
        case TimeValidityUnit.year:
          return timeValidity.amount! * 60 * 24 * 365;
        case TimeValidityUnit.unlimited:
          return Number.MAX_SAFE_INTEGER; // Only for comparison
        default:
          return 0;
      }
    case TimeValidityType.fromToDate:
      return timeValidity.toDate?.diff(timeValidity.fromDate!, 'minutes').minutes ?? 0;
    case TimeValidityType.fromToDateTime:
      return timeValidity.toDateTime?.diff(timeValidity.fromDateTime!, 'minutes').minutes ?? 0;
    default:
      return 0;
  }
}

function createZonesDisplayText(tariffZones: ITariffZone[]): string {
  const abc = tariffZones.filter((z) => z.number === 3000 || z.number === 3001 || z.number === 3002).map((z) => z.shortName);
  abc.sort();
  const notAbc = tariffZones
    .filter((z) => !(z.number === 3000 || z.number === 3001 || z.number === 3002))
    .map((z) => z.name)
    .map((zoneName) => zoneName.replace('  ', ' ')); // Fix for zones with double spaces, e.g. 'Varberg  B' which messes up the sorting
  notAbc.sort();
  return (abc.length > 0 ? [abc.join('')] : []).concat(notAbc).join(', ');
}

function TicketPrices({ guid }: { guid: string }) {
  const [selectedTravellerCategory, setSelectedTravellerCategory] = useState(ConfigurationService.get(`ticketprices.travellercategory.${guid}`) as TravellerCategory);
  const [showAll, setShowAll] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.type);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ascending);

  const products = (JSON.parse(ConfigurationService.get(`ticketprices.products.${guid}`)!) as ProductViewModel[]).map(ProductViewModel.mapToBusinessModel);

  const showShowMoreButton = !showAll && products.filter((tp) => tp.travellerCategory === selectedTravellerCategory).length > 10;

  const sortedProducts: IProduct[] = [...products.filter((tp) => tp.travellerCategory === selectedTravellerCategory).slice(0, showAll ? Number.MAX_SAFE_INTEGER : 10)].sort((a, b) => {
    let result = 0;
    if (sortBy === SortBy.type) {
      result = translateProductType(a.productType).localeCompare(translateProductType(b.productType));
    }
    if (sortBy === SortBy.validitytime) {
      result = convertTimeValidityToMinutes(a.timeValidity) - convertTimeValidityToMinutes(b.timeValidity);
    }
    if (sortBy === SortBy.zone) {
      result = createZonesDisplayText(a.tariffZones).localeCompare(createZonesDisplayText(b.tariffZones));
    }
    if (sortBy === SortBy.price) {
      result = (a.priceInSek ?? 0) - (b.priceInSek ?? 0);
    }
    return result * (sortDirection === SortDirection.ascending ? 1 : -1);
  });

  function handleOnClick(column: SortBy): void {
    if (column === sortBy) {
      setSortDirection(sortDirection === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending);
    } else {
      setSortBy(column);
      setSortDirection(SortDirection.ascending);
    }
  }

  function ColumnHeader({ column, buttonTextTranslationSuffix }: { column: SortBy; buttonTextTranslationSuffix: string }) {
    return (
      <th scope="col" aria-sort={getAriaSortValue(column, sortBy, sortDirection)}>
        <button type="button" className="btn--unstyled text-nowrap" onClick={() => handleOnClick(column)}>
          {TranslationService.get(`common.ticketprices.table.${buttonTextTranslationSuffix}`)}
          <span aria-hidden="true"></span>
        </button>
      </th>
    );
  }

  function ProductTypeValue({ value }: { value: ProductType }) {
    return <span>{translateProductType(value)}</span>;
  }

  function TimeValidityValue({ value }: { value: ITimeValidity }) {
    if (value.type === TimeValidityType.amountUnit) {
      return <span>{`${value.amount} ${TranslationService.get(`common.products.timevalidity.units.${value.unit.toLocaleLowerCase()}`)}`}</span>;
    }
    if (value.type === TimeValidityType.fromToDate) {
      return <span>{`${value.fromDate?.toLocaleString(DateTime.DATE_SHORT)} - ${value.toDate?.toLocaleString(DateTime.DATE_SHORT)}`}</span>;
    }
    if (value.type === TimeValidityType.fromToDateTime) {
      return <span>{`${value.fromDateTime?.toLocaleString(DateTime.DATETIME_SHORT)} - ${value.toDateTime?.toLocaleString(DateTime.DATETIME_SHORT)}`}</span>;
    }
    return <span>{JSON.stringify(value)}</span>;
  }

  return (
    <div className="ticket-prices-block" aria-live="polite">
      <h3>{TranslationService.get('common.ticketprices.travellercategory')}</h3>
      <div className="mb-3">
        <TravellerCategorySelector renderAsDropdown={false} selectedTravellerCategory={selectedTravellerCategory} onChange={setSelectedTravellerCategory}></TravellerCategorySelector>
      </div>
      <div className="small font-italic mb-3">{TranslationService.get('common.ticketprices.travellercategorytext')}</div>
      <div className="table-responsive-sm">
        <table className="table table--sortable table--caption-visually-hidden mb-0">
          <caption>{TranslationService.get('common.ticketprices.table.caption')}</caption>
          <thead>
            <tr>
              <ColumnHeader column={SortBy.type} buttonTextTranslationSuffix="ticket" />
              <ColumnHeader column={SortBy.validitytime} buttonTextTranslationSuffix="validitytime" />
              <ColumnHeader column={SortBy.zone} buttonTextTranslationSuffix="zone" />
              <ColumnHeader column={SortBy.price} buttonTextTranslationSuffix="price" />
            </tr>
          </thead>
          <tbody>
            {sortedProducts.map((tp) => (
              <tr key={tp.productId}>
                <td>
                  <ProductTypeValue value={tp.productType} />
                </td>
                <td>
                  <TimeValidityValue value={tp.timeValidity} />
                </td>
                <td>{createZonesDisplayText(tp.tariffZones)}</td>
                <td>{tp.priceInSek && <Currency amount={tp.priceInSek} showFractionDigits={false} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedProducts.length === 0 && <div className="mt-2 mx-2 font-italic">{TranslationService.get('common.ticketprices.nodatatext')}</div>}
      <div>
        {showShowMoreButton && (
          <div className="mt-3">
            <ShowMoreButton ariaControls="blog-list-items" onClick={() => setShowAll(true)}></ShowMoreButton>
          </div>
        )}
      </div>
    </div>
  );
}

export default TicketPrices;
